import { useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { clsx } from 'clsx';
import { Card } from '@/shared/ui/Card';
import { Skeleton } from '@/shared/ui/Skeleton';
import { FEATURE_GATES, PRACTICE_FEATURE_FLAGS, type FeatureFlag } from '@/config/features';
import type { ModuleKey } from '../../api/supervisionApi';
import type { Location, PracticeDetail } from '../../api/agencyApi';
import { usePractice } from '../../hooks/usePractices';
import { usePracticePulse } from '../../hooks/usePortfolio';
import { ReviewFunnelTab } from '../commandCenter/ReviewFunnelTab';
import { AdConfigTab } from '../adConfig/AdConfigTab';
import { VapiCallsCard } from '../settings/VapiCallsCard';
import { MetaAdAccountPicker } from '../MetaAdAccountPicker';
import {
  AuditStatusStrip,
  FeatureFlagsCard,
  FeatureResetsCard,
  RunActionsCard,
} from '../admin/AdminCards';
import { MetaIntegrationCard } from './MetaIntegrationCard';
import { CampaignsToCheckCard } from './CampaignsToCheckCard';
import { AdOutcomesCard } from './AdOutcomesCard';
import { AdBudgetCard } from '@/modules/settings/components/paid/AdBudgetCard';
import { ChangeRulesCard } from '@/modules/settings/components/paid/ChangeRulesCard';
import { ReviewModeControl } from './ReviewModeControl';
import { ReviewSmsStatusPill } from './ReviewSmsStatusPill';
import { ApprovalPreferenceToggle } from './ApprovalPreferenceToggle';
import { CommunityModulePanel } from './CommunityModulePanel';
import { ReferralsModulePanel } from './ReferralsModulePanel';
import styles from './ModulesTab.module.css';

/**
 * Practice › Modules: every module-specific control — the insight, how it
 * runs, force-a-run, permissions, resets — lives with its module, scoped to
 * one location. The nav splits modules into the ones this practice uses and
 * the rest; nothing here switches a module on or off, because the product has
 * no such switch today (use reflects what the practice has set up).
 */

/**
 * The nav's own key set. It is wider than `ModuleKey`: content, brand kit,
 * print & mail and channel mix are not supervision modules yet,
 * but the operator still needs somewhere to see their gate status.
 */
type NavKey =
  | 'reviews'
  | 'visibility'
  | 'ads'
  | 'community'
  | 'content'
  | 'strategy'
  | 'insurance'
  | 'safety'
  | 'referrals'
  | 'calls'
  | 'brand-kit'
  | 'print-mail'
  | 'channel-mix';

type ModuleDef = {
  key: NavKey;
  label: string;
  /** Present when the module is switched on per practice from `config/features.ts`. */
  gate?: FeatureFlag;
};

const MODULES: readonly ModuleDef[] = [
  { key: 'reviews', label: 'Reviews' },
  { key: 'visibility', label: 'Online visibility' },
  { key: 'ads', label: 'Ads' },
  // Community & Referrals (Joe): "in use" is the stored flag itself, not a live signal.
  { key: 'community', label: 'Community', gate: 'community' },
  { key: 'content', label: 'Content' },
  { key: 'strategy', label: 'Strategy' },
  { key: 'insurance', label: 'Insurance verification' },
  { key: 'safety', label: 'Safety & training', gate: 'safety-training' },
  // Patient Referrals: "in use" is the stored flag itself, not a live signal.
  { key: 'referrals', label: 'Patient referrals', gate: 'patient-referrals' },
  { key: 'calls', label: 'Calls', gate: 'calls' },
  { key: 'brand-kit', label: 'Brand kit', gate: 'brand-kit' },
  { key: 'print-mail', label: 'Print & mail', gate: 'print-mail' },
  { key: 'channel-mix', label: 'Channel mix', gate: 'channel-mix' },
];

const DEFAULT_MODULE: NavKey = 'reviews';

function parseModule(value: string | null): NavKey {
  return MODULES.some((m) => m.key === value) ? (value as NavKey) : DEFAULT_MODULE;
}

/** Action keys in `AI_ACTIONS` that belong to each module's "Force a run" card. */
const RUN_ACTIONS = {
  visibility: ['recrawl-site', 'run-site-audit'],
  ads: ['recommend-campaigns', 'run-ads-audit', 'generate-campaign-recs'],
  content: ['generate-content-plan'],
  strategy: ['generate-strategy-insights'],
} as const;

export function ModulesTab({
  practiceId,
  locationId,
  modulesInUse,
  onOpenSettings,
}: {
  practiceId: string;
  locationId: string;
  modulesInUse: ModuleKey[];
  /** Where the ad-config file card sends the operator to fix a missing connection. */
  onOpenSettings?: () => void;
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const selected = parseModule(searchParams.get('module'));
  const { data: practice, isLoading } = usePractice(practiceId);

  const selectModule = useCallback(
    (key: NavKey) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('module', key);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const inUse = new Set<string>(modulesInUse);
  if (practice?.featureFlags?.community === true) inUse.add('community');
  if (practice?.featureFlags?.patientReferrals === true) inUse.add('referrals');
  const isInUse = (key: NavKey) => inUse.has(key);
  const used = MODULES.filter((m) => isInUse(m.key));
  const off = MODULES.filter((m) => !isInUse(m.key));
  const current = MODULES.find((m) => m.key === selected) ?? MODULES[0];

  const location = practice?.locations.find((l) => l.id === locationId) ?? null;

  return (
    <div className={styles.layout}>
      <nav className={styles.nav} aria-label="Modules">
        <NavGroup
          label="In use"
          modules={used}
          tone="on"
          selected={selected}
          onSelect={selectModule}
        />
        <NavGroup
          label="Off"
          modules={off}
          tone="off"
          selected={selected}
          onSelect={selectModule}
        />
      </nav>

      <div className={styles.body}>
        <ModuleHeader
          module={current}
          practiceId={practiceId}
          inUse={isInUse(current.key)}
          locationName={location?.name ?? null}
          flags={practice?.featureFlags ?? {}}
          flagsLoading={isLoading}
        />
        {isLoading ? (
          <Skeleton height={240} borderRadius="var(--card-radius)" />
        ) : !practice ? (
          <div className={styles.empty}>Practice not found.</div>
        ) : !location ? (
          <div className={styles.empty}>
            This location is no longer on the practice. Pick another one above.
          </div>
        ) : (
          <ModuleBody
            module={current.key}
            practice={practice}
            location={location}
            onOpenSettings={onOpenSettings}
          />
        )}
      </div>
    </div>
  );
}

// ─── Nav ──────────────────────────────────────────────────────────────────────

function NavGroup({
  label,
  modules,
  tone,
  selected,
  onSelect,
}: {
  label: string;
  modules: readonly ModuleDef[];
  tone: 'on' | 'off';
  selected: NavKey;
  onSelect: (key: NavKey) => void;
}) {
  if (modules.length === 0) return null;
  return (
    <div className={styles.navGroup}>
      <div className={styles.navGroupLabel}>{label}</div>
      {modules.map((m) => (
        <button
          key={m.key}
          type="button"
          className={clsx(styles.navItem, selected === m.key && styles.navItemActive)}
          aria-current={selected === m.key ? 'page' : undefined}
          onClick={() => onSelect(m.key)}
        >
          <span className={clsx(styles.dot, tone === 'on' ? styles.dotOn : styles.dotOff)} />
          {m.label}
        </button>
      ))}
    </div>
  );
}

// ─── Header ───────────────────────────────────────────────────────────────────

/**
 * A gated module's real on/off state: a flag with a `PRACTICE_FEATURE_FLAGS` storage key reads the
 * practice's stored registry value (Command Center Admin tab); everything else falls back to the
 * static `FEATURE_GATES` allow-list. Mirrors `useFeatureFlagState`'s precedence — this can't just
 * call that hook because it needs an arbitrary `practiceId` (an agency admin viewing another
 * practice), not the logged-in/impersonated one the hook is bound to. Returns `null` while a
 * registry-backed flag's stored value is still loading, so the header doesn't show a wrong "not on
 * the allow-list" before the fetch resolves — same "unknown, not off" rule `FeatureRoute` follows.
 */
function isModuleAllowed(
  gate: FeatureFlag,
  practiceId: string,
  flags: Record<string, boolean>,
  flagsLoading: boolean,
): boolean | null {
  const storedKey = PRACTICE_FEATURE_FLAGS[gate];
  if (storedKey) {
    if (flagsLoading) return null;
    return flags[storedKey] === true;
  }
  const gateList = FEATURE_GATES[gate];
  return gateList.includes('*') || gateList.includes(practiceId);
}

function ModuleHeader({
  module,
  practiceId,
  inUse,
  locationName,
  flags,
  flagsLoading,
}: {
  module: ModuleDef;
  practiceId: string;
  inUse: boolean;
  locationName: string | null;
  flags: Record<string, boolean>;
  flagsLoading: boolean;
}) {
  const registryBacked = module.gate ? Boolean(PRACTICE_FEATURE_FLAGS[module.gate]) : false;
  const allowed = module.gate
    ? isModuleAllowed(module.gate, practiceId, flags, flagsLoading)
    : null;
  return (
    <Card>
      <div className={styles.header}>
        <div className={styles.headerRow}>
          <h2 className={styles.headerTitle}>{module.label}</h2>
          <span className={clsx(styles.pill, inUse ? styles.pillOn : styles.pillOff)}>
            {inUse ? 'In use' : 'Not in use'}
          </span>
          {locationName && <span className={styles.scope}>{locationName}</span>}
        </div>
        {allowed !== null && (
          <div className={styles.gate}>
            <span>
              {allowed
                ? 'This practice is on the allow-list.'
                : 'This practice is not on the allow-list.'}
            </span>
            <span className={styles.gateNote}>
              {registryBacked
                ? 'Switched on per practice from the Command Center Admin tab.'
                : 'Switched on per practice in code today (config/features.ts).'}
            </span>
          </div>
        )}
      </div>
    </Card>
  );
}

// ─── Bodies ───────────────────────────────────────────────────────────────────

function ModuleBody({
  module,
  practice,
  location,
  onOpenSettings,
}: {
  module: NavKey;
  practice: PracticeDetail;
  location: Location;
  onOpenSettings?: () => void;
}) {
  const practiceId = practice.id;
  const locations = practice.locations;
  const flags = practice.featureFlags ?? {};

  switch (module) {
    case 'reviews':
      return (
        <>
          {flags.reviewsSms === true && (
            <div className={styles.rows}>
              <ReviewSmsStatusPill practiceId={practiceId} locationId={location.id} />
            </div>
          )}
          <ReviewFunnelTab
            practiceId={practiceId}
            locationId={location.id}
            textAvailable={flags.reviewsSms === true}
          />
          <Card title="How it runs">
            <div className={styles.rows}>
              <ReviewModeControl practiceId={practiceId} location={location} />
            </div>
          </Card>
          <FeatureFlagsCard
            practiceId={practiceId}
            flags={flags}
            only="reviews"
            title="Permissions (practice-wide)"
          />
          <FeatureResetsCard
            practiceId={practiceId}
            locations={locations}
            locationId={location.id}
            only="reviews"
          />
        </>
      );

    case 'visibility':
      return (
        <>
          <Card title="Latest site audit">
            <AuditStatusStrip practiceId={practiceId} locationId={location.id} />
            <p className={styles.note}>
              Re-crawl when the practice has changed their site; re-run the audit when fixes are
              live and the crawl is still current.
            </p>
          </Card>
          <RunActionsCard
            title="Force a run"
            practiceId={practiceId}
            locations={locations}
            locationId={location.id}
            actions={RUN_ACTIONS.visibility}
          />
        </>
      );

    case 'ads':
      return (
        <>
          <AdsLast30Card practiceId={practiceId} />
          <MetaIntegrationCard />
          <CampaignsToCheckCard practiceId={practiceId} locationId={location.id} />
          <AdOutcomesCard practiceId={practiceId} locationId={location.id} />
          {/* The practice's brand terms and auction insights are in its Settings > Paid; the agency's part is here. */}
          <AdBudgetCard locationId={location.id} />
          <ChangeRulesCard locationId={location.id} unlock="open" />
          <Card title="Account & how it runs">
            <div className={styles.rows}>
              <MetaAdAccountPicker locationId={location.id} />
              <ApprovalPreferenceToggle
                practiceId={practiceId}
                location={location}
                kind="campaign"
              />
            </div>
          </Card>
          <Card title="Ad config file">
            <AdConfigTab
              key={location.id}
              practiceId={practiceId}
              practiceName={practice.name}
              locations={[{ id: location.id, name: location.name }]}
              onOpenSetup={onOpenSettings ?? noop}
            />
          </Card>
          <RunActionsCard
            title="Force a run"
            practiceId={practiceId}
            locations={locations}
            locationId={location.id}
            actions={RUN_ACTIONS.ads}
          />
          <FeatureFlagsCard
            title="Permissions (practice-wide)"
            practiceId={practiceId}
            flags={flags}
            only="ads"
          />
        </>
      );

    case 'community':
      return (
        <CommunityModulePanel practiceId={practiceId} locationId={location.id} flags={flags} />
      );

    case 'content':
      return (
        <>
          <Card title="How it runs">
            <div className={styles.rows}>
              <ApprovalPreferenceToggle
                practiceId={practiceId}
                location={location}
                kind="content"
              />
            </div>
          </Card>
          <RunActionsCard
            title="Force a run"
            practiceId={practiceId}
            locations={locations}
            locationId={location.id}
            actions={RUN_ACTIONS.content}
          />
        </>
      );

    case 'strategy':
      return (
        <>
          <div>
            <div className={styles.kicker}>Practice-wide</div>
            <VapiCallsCard practiceId={practiceId} />
          </div>
          <RunActionsCard
            title="Force a run"
            practiceId={practiceId}
            locations={locations}
            locationId={location.id}
            actions={RUN_ACTIONS.strategy}
          />
        </>
      );

    case 'insurance':
      return (
        <>
          <FeatureFlagsCard
            title="Permissions (practice-wide)"
            practiceId={practiceId}
            flags={flags}
            only="insurance"
          />
          <Card title="Payers">
            <p className={styles.note}>
              Payers are managed under <Link to="/agency/insurance-payers">Insurance Payers</Link>{' '}
              in the sidebar — they are shared across every practice, not set here.
            </p>
          </Card>
        </>
      );

    case 'safety':
      return (
        <>
          <FeatureFlagsCard
            title="Permissions (practice-wide)"
            practiceId={practiceId}
            flags={flags}
            only="safety"
          />
          <FeatureResetsCard
            practiceId={practiceId}
            locations={locations}
            locationId={location.id}
            only="safety"
          />
        </>
      );

    case 'referrals':
      return <ReferralsModulePanel practiceId={practiceId} flags={flags} />;

    case 'calls':
    case 'brand-kit':
    case 'print-mail':
    case 'channel-mix':
      return null;
  }
}

const noop = () => undefined;

// ─── Ads · last 30 days ───────────────────────────────────────────────────────

function AdsLast30Card({ practiceId }: { practiceId: string }) {
  const { data: pulse, isLoading } = usePracticePulse(practiceId);

  if (isLoading) return <Skeleton height={96} borderRadius="var(--card-radius)" />;
  const row = pulse?.row;
  if (!row) return null;

  if (row.spend30 === 0) {
    return (
      <Card title="Last 30 days">
        <p className={styles.note}>Not running · no campaign data yet</p>
      </Card>
    );
  }

  const cpaOver = row.cpa !== null && row.cpaBaseline !== null && row.cpa > row.cpaBaseline * 1.15;

  return (
    <Card title="Last 30 days" subtitle="Practice-wide across connected ad accounts">
      <div className={styles.stats}>
        <div className={styles.stat}>
          <span className={styles.statLabel}>Spend</span>
          <span className={styles.statValue}>${row.spend30.toLocaleString()}</span>
        </div>
        <div className={styles.stat}>
          <span className={styles.statLabel}>Leads</span>
          <span className={styles.statValue}>{row.leads30.toLocaleString()}</span>
          <span className={styles.statSub}>conversions</span>
        </div>
        <div className={styles.stat}>
          <span className={styles.statLabel}>Cost per lead</span>
          <span className={clsx(styles.statValue, cpaOver && styles.statValueOver)}>
            {row.cpa === null ? '—' : `$${row.cpa.toLocaleString()}`}
          </span>
          <span className={styles.statSub}>
            {row.cpaBaseline === null
              ? 'no baseline yet'
              : `baseline $${row.cpaBaseline.toLocaleString()}`}
          </span>
        </div>
      </div>
    </Card>
  );
}
